import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from 'app/components/ui/dialog';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'app/components/ui/select';
import { Textarea } from 'app/components/ui/textarea';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from 'app/components/ui/card';
import { Badge } from 'app/components/ui/badge';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import { useInventorySlice } from '../slice';
import {
    InventoryAdjustmentFormProps,
    InventoryAdjustment,
} from 'types/inventory';
import { toast } from 'sonner';
import { Loader2, Package, TrendingUp, TrendingDown } from 'lucide-react';

const InventoryAdjustmentDialog: React.FC<InventoryAdjustmentFormProps> = ({
    productId,
    onSuccess,
    onCancel,
}) => {
    const [formData, setFormData] = useState<InventoryAdjustment>({
        action_type: 'adjustment',
        quantity_change: 0,
        unit_cost: undefined,
        reference_number: '',
        notes: '',
        performed_by: 'admin',
    });

    const {
        useAdjustProductInventoryMutation,
        useGetProductInventoryDetailsQuery,
    } = useInventorySlice();

    const [adjustInventory, { isLoading }] = useAdjustProductInventoryMutation();
    const { data: productDetails, isLoading: productLoading } =
        useGetProductInventoryDetailsQuery(productId);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.quantity_change === 0) {
            toast.error('Quantity change cannot be zero');
            return;
        }

        if (formData.action_type === 'sale' && formData.quantity_change > 0) {
            toast.error('Sale quantity should be negative');
            return;
        }

        if (formData.action_type === 'return' && formData.quantity_change < 0) {
            toast.error('Return quantity should be positive');
            return;
        }

        try {
            await adjustInventory({
                productId,
                adjustment: formData,
            }).unwrap();

            toast.success('Inventory adjusted successfully');
            onSuccess?.();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to adjust inventory');
        }
    };

    const handleQuantityChange = (value: string) => {
        const numValue = parseInt(value) || 0;
        setFormData(prev => ({ ...prev, quantity_change: numValue }));
    };

    const getActionTypeIcon = (actionType: string) => {
        switch (actionType) {
            case 'adjustment':
                return <Package className="h-4 w-4" />;
            case 'sale':
                return <TrendingDown className="h-4 w-4" />;
            case 'return':
                return <TrendingUp className="h-4 w-4" />;
            case 'damage':
                return <Package className="h-4 w-4" />;
            case 'transfer':
                return <Package className="h-4 w-4" />;
            default:
                return <Package className="h-4 w-4" />;
        }
    };

    const getActionTypeColor = (actionType: string) => {
        switch (actionType) {
            case 'adjustment':
                return 'bg-blue-100 text-blue-800';
            case 'sale':
                return 'bg-green-100 text-green-800';
            case 'return':
                return 'bg-purple-100 text-purple-800';
            case 'damage':
                return 'bg-red-100 text-red-800';
            case 'transfer':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (productLoading) {
        return (
            <Dialog open onOpenChange={onCancel}>
                <DialogContent className="sm:max-w-md">
                    <div className="flex items-center justify-center h-32">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    const product = productDetails?.product;
    const currentStock = product?.stock_quantity || 0;
    const newStock = currentStock + formData.quantity_change;

    return (
        <Dialog open onOpenChange={onCancel}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Adjust Inventory</DialogTitle>
                    <DialogDescription>
                        Adjust inventory for {product?.product_name}
                    </DialogDescription>
                </DialogHeader>

                {/* Product Info */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium">
                            Product Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Product:</span>
                            <span className="text-sm">{product?.product_name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">SKU:</span>
                            <span className="text-sm">{product?.sku}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Current Stock:</span>
                            <Badge variant="outline">{currentStock} units</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Price:</span>
                            <span className="text-sm">${product?.sale_price}</span>
                        </div>
                    </CardContent>
                </Card>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Action Type */}
                    <div className="space-y-2">
                        <Label htmlFor="action_type">Action Type</Label>
                        <Select
                            value={formData.action_type}
                            onValueChange={value =>
                                setFormData(prev => ({ ...prev, action_type: value as any }))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select action type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="adjustment">
                                    <div className="flex items-center gap-2">
                                        {getActionTypeIcon('adjustment')}
                                        <span>Adjustment</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="sale">
                                    <div className="flex items-center gap-2">
                                        {getActionTypeIcon('sale')}
                                        <span>Sale</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="return">
                                    <div className="flex items-center gap-2">
                                        {getActionTypeIcon('return')}
                                        <span>Return</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="damage">
                                    <div className="flex items-center gap-2">
                                        {getActionTypeIcon('damage')}
                                        <span>Damage</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value="transfer">
                                    <div className="flex items-center gap-2">
                                        {getActionTypeIcon('transfer')}
                                        <span>Transfer</span>
                                    </div>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Quantity Change */}
                    <div className="space-y-2">
                        <Label htmlFor="quantity_change">Quantity Change</Label>
                        <Input
                            id="quantity_change"
                            type="number"
                            value={formData.quantity_change}
                            onChange={e => handleQuantityChange(e.target.value)}
                            placeholder="Enter quantity change"
                            required
                        />
                        <p className="text-xs text-muted-foreground">
                            Positive for additions, negative for reductions
                        </p>
                    </div>

                    {/* Unit Cost */}
                    <div className="space-y-2">
                        <Label htmlFor="unit_cost">Unit Cost (Optional)</Label>
                        <Input
                            id="unit_cost"
                            type="number"
                            step="0.01"
                            value={formData.unit_cost || ''}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    unit_cost: parseFloat(e.target.value) || undefined,
                                }))
                            }
                            placeholder="Enter unit cost"
                        />
                    </div>

                    {/* Reference Number */}
                    <div className="space-y-2">
                        <Label htmlFor="reference_number">
                            Reference Number (Optional)
                        </Label>
                        <Input
                            id="reference_number"
                            value={formData.reference_number}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    reference_number: e.target.value,
                                }))
                            }
                            placeholder="Order ID, PO number, etc."
                        />
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                        <Label htmlFor="notes">Notes (Optional)</Label>
                        <Textarea
                            id="notes"
                            value={formData.notes}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, notes: e.target.value }))
                            }
                            placeholder="Additional notes about this adjustment"
                            rows={3}
                        />
                    </div>

                    {/* Stock Preview */}
                    {formData.quantity_change !== 0 && (
                        <Alert>
                            <AlertDescription>
                                <div className="flex items-center justify-between">
                                    <span>Stock will change from:</span>
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline">{currentStock}</Badge>
                                        <span>→</span>
                                        <Badge
                                            variant={
                                                newStock < 0
                                                    ? 'destructive'
                                                    : newStock < 10
                                                        ? 'secondary'
                                                        : 'default'
                                            }
                                        >
                                            {newStock}
                                        </Badge>
                                    </div>
                                </div>
                                {newStock < 0 && (
                                    <p className="text-sm text-red-600 mt-1">
                                        Warning: This will result in negative stock!
                                    </p>
                                )}
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading || formData.quantity_change === 0}
                        >
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Adjust Inventory
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default InventoryAdjustmentDialog;
